import { DatabaseZap, RefreshCw } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { dateTime, num } from '@/lib/format'

import { lmsErrorDetail, useLmsRefreshAll, useLmsStatus } from '@/features/lms/api'

/**
 * Dashboard widget for Graviton-tier tenants: which LMS the dues come from,
 * how the last refresh went, and a button to re-read every open case now.
 * The nightly run does the same; the button is for "a customer just paid at
 * the branch" and for demos.
 */
export function LmsSyncCard({ canRefresh }: { canRefresh: boolean }) {
  const { data, isPending } = useLmsStatus()
  const refresh = useLmsRefreshAll()
  const run = data?.lastRun ?? null

  const refreshError = refresh.isError
    ? lmsErrorDetail(refresh.error).includes('lms_not_configured')
      ? 'The LMS is not set up yet — choose a mode in Settings.'
      : 'Refresh failed.'
    : null

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DatabaseZap className="h-4 w-4" />
              LMS sync
              {data?.mode === 'MOCK' && <Badge tone="warning">Mock LMS</Badge>}
              {data?.mode === 'LIVE' && <Badge tone="positive">Live</Badge>}
              {data?.mode === 'UNCONFIGURED' && <Badge tone="slate">Not connected</Badge>}
            </CardTitle>
            <CardDescription>
              Customers, loans and dues come from your LMS, refreshed nightly and whenever a case is opened.
            </CardDescription>
          </div>
          {canRefresh && (
            <Button
              size="sm"
              variant="outline"
              disabled={refresh.isPending || data?.mode === 'UNCONFIGURED'}
              onClick={() => refresh.mutate()}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {refresh.isPending ? 'Refreshing…' : 'Refresh from LMS now'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        {isPending ? (
          <Skeleton className="h-5 w-72" />
        ) : !run ? (
          <p className="text-muted-foreground">
            {data?.mode === 'UNCONFIGURED'
              ? 'Choose an LMS mode in Settings to start syncing.'
              : 'No refresh has run yet.'}
          </p>
        ) : (
          <p className="flex flex-wrap items-center gap-2 text-navy-900">
            <span className="tabular-nums">
              Last refresh {dateTime(run.startedAt)} · {num(run.refreshed)} refreshed ·{' '}
              {num(run.closed)} closed · {num(run.failed)} failed
            </span>
            <Badge tone="navy">{run.trigger === 'NIGHTLY' ? 'Nightly' : 'Manual'}</Badge>
            {!run.finishedAt && <Badge tone="info">Running</Badge>}
          </p>
        )}
        {run?.lastError && <p className="text-xs text-negative">{run.lastError}</p>}
        {refreshError && <p className="text-xs text-negative">{refreshError}</p>}
      </CardContent>
    </Card>
  )
}
